import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { AlyzeOptimizedImage } from '../../../../utilities/AlyzeOptimizedImage';
import { ArrowUpRight20Filled } from '@fluentui/react-icons';
import { Link } from 'react-router-dom';

const useStyles = makeStyles({
    featureCard: {
        width: '509.33px',
        height: '640px',
        padding: '24px',
        borderRadius: '40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        position: 'relative',
        overflow: 'hidden',
        '@media (max-width: 1200px)': {
            width: '100%',
            maxWidth: '400px',
        },
        '@media (max-width: 768px)': {
            height: '540px',
            padding: '16px',
            borderRadius: '24px',
        },
    },

    backgroundImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
    },

    badgeContainer: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'center',
        display: 'flex',
        position: 'relative',
        zIndex: 2,
    },

    badge: {
        paddingLeft: '20px',
        paddingRight: '20px',
        paddingTop: '12px',
        paddingBottom: '12px',
        backgroundColor: '#C6A972',
        borderRadius: '20px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        display: 'flex',
    },

    badgeText: {
        color: 'white',
        fontSize: '12px',
        fontFamily: 'Figtree',
        fontWeight: 700,
        lineHeight: '14.40px',
        letterSpacing: '0.48px',
        wordWrap: 'break-word',
    },

    cardContent: {
        alignSelf: 'stretch',
        padding: '40px',
        backgroundColor: 'rgba(19, 10, 0, 0.50)',
        borderRadius: '32px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '28px',
        position: 'relative',
        zIndex: 2,
        '@media (max-width: 768px)': {
            padding: '32px',
            gap: '24px',
        },
    },

    cardInfo: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '8px',
    },

    cardTitle: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'center',
        display: 'flex',
    },

    cardTitleText: {
        color: '#F4F0E7',
        fontSize: '32px',
        fontFamily: 'Behind The Nineties',
        fontStyle: 'italic',
        fontWeight: 500,
        lineHeight: '44.80px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '28px',
            lineHeight: '36px',
        },
    },

    cardDescription: {
        alignSelf: 'stretch',
        color: '#F4F0E7',
        fontSize: '20px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '28px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '18px',
            lineHeight: '26px',
        },
    },

    cardButton: {
        alignSelf: 'stretch',
        padding: '16px',
        backgroundColor: '#FFFCF5',
        borderRadius: '40px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        display: 'flex',
        cursor: 'pointer',
        border: 'none',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
    },

    cardButtonText: {
        color: '#222222',
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: 700,
        lineHeight: '16px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '14px',
        },
    },

    arrowIcon: {
        color: '#222222',
        fontSize: '20px',
    },
});

export interface AlyzeFeatureCardProps {
    backgroundImage: string;
    title: string;
    description: React.ReactNode;
    buttonText: string;
    buttonLink: string;
    showBadge?: boolean;
    badgeText?: string;
    showCardButton?: boolean;
}


const AlyzeFeatureCard: React.FC<AlyzeFeatureCardProps> = ({
    backgroundImage,
    title,
    description,
    buttonText,
    buttonLink,
    showBadge = false,
    badgeText = 'POPULAR',
    showCardButton = true,
}) => {
    const styles = useStyles();

    return (
        <div className={styles.featureCard}>
            <div className={styles.backgroundImage}>
                <AlyzeOptimizedImage
                    src={backgroundImage}
                    alt={title}
                    width={509}
                    height={640}
                />
            </div>
            
            {showBadge ? (
                <div className={styles.badgeContainer}>
                    <div className={styles.badge}>
                        <div className={styles.badgeText}>{badgeText}</div>
                    </div>
                </div>
            ) : (
                <div style={{ height: '0' }}></div>
            )}
            
            <div className={styles.cardContent}>
                <div className={styles.cardInfo}>
                    <div className={styles.cardTitle}>
                        <div className={styles.cardTitleText}>{title}</div>
                    </div>
                    <div className={styles.cardDescription}>
                        {description}
                    </div>
                </div>
                {showCardButton && (
                    <Link to={buttonLink} className={styles.cardButton}>
                        <div className={styles.cardButtonText}>{buttonText}</div>
                        <ArrowUpRight20Filled className={styles.arrowIcon} />
                    </Link>
                )}
            </div>
        </div>
    );
};

export { AlyzeFeatureCard };
